import { LockKeyhole } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { type PairedDevice, listPairedDevices, revokePairedDevice } from "../../computers/access"
import { type PairingSession, useComputers } from "../../computers/context"
import { useOptionalCodeStore } from "../../store/context"
import { AddComputerTile } from "./AddComputerTile"
import { AddDevicePanel } from "./AddDevicePanel"
import { LocalComputerCard, RemoteComputerCard, reachabilityFor } from "./ComputerCard"
import { IdentityHero } from "./IdentityHero"
import { PeopleList } from "./PeopleList"
import { Section } from "./Section"
import { SharedWithYou } from "./SharedWithYou"

interface RemoteAccessState {
    enabled: boolean
    active: boolean
    relayUrl?: string
}

/**
 * Settings → Sharing. Four questions, top to bottom: who am I, which
 * computers are mine, who can reach them, and what has been opened to me.
 * The relay explanation is a footnote, not a preamble.
 */
export function SharingTab() {
    const computers = useComputers()
    const codeStore = useOptionalCodeStore()
    const api = window.wildlarkAPI?.nest.cloudMode
    const localComputer = useMemo(() => computers.state.computers.find((computer) => computer.kind === "local"), [computers.state.computers])
    // People are managed on any computer this client administers: Electron's
    // This Mac, or a browser's paired computer when its token is node-wide.
    const ownedComputer = useMemo(() => {
        if (localComputer) return localComputer
        const access = codeStore?.access
        if (!access || access.state !== "ready" || !access.node) return undefined
        return computers.state.computers.find((computer) => computer.nodeId === computers.scope)
    }, [codeStore?.access, computers.scope, computers.state.computers, localComputer])
    const sharedNodeIds = useMemo(() => new Set(computers.people.map((person) => person.nodeId)), [computers.people])
    const ownRemoteComputers = computers.state.computers.filter((computer) => computer.kind === "remote" && !sharedNodeIds.has(computer.nodeId))

    const [state, setState] = useState<RemoteAccessState | null>(null)
    const [pairing, setPairing] = useState<PairingSession | null>(null)
    const [devices, setDevices] = useState<PairedDevice[]>([])
    const [saving, setSaving] = useState(false)
    const [restarting, setRestarting] = useState(false)
    const [pairingBusy, setPairingBusy] = useState(false)
    const [devicesBusy, setDevicesBusy] = useState(false)
    const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null)
    const [confirmTokenId, setConfirmTokenId] = useState<string | null>(null)
    const [relayDetails, setRelayDetails] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const deviceRefreshInFlight = useRef<Promise<void> | null>(null)

    useEffect(() => {
        if (!api) return
        let cancelled = false
        api.get().then(
            (next) => {
                if (!cancelled) setState(next)
            },
            (cause: unknown) => {
                if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause))
            }
        )
        return () => {
            cancelled = true
        }
    }, [api])

    const refreshDevices = useCallback(async () => {
        if (deviceRefreshInFlight.current) return deviceRefreshInFlight.current
        if (!state?.active || !localComputer) {
            setDevices([])
            return
        }
        const refresh = (async () => {
            setDevicesBusy(true)
            setError(null)
            try {
                setDevices(await listPairedDevices(computers.backend, localComputer.nodeId))
            } catch (cause) {
                setDevices([])
                setError(cause instanceof Error ? cause.message : String(cause))
            } finally {
                setDevicesBusy(false)
            }
        })()
        deviceRefreshInFlight.current = refresh
        try {
            await refresh
        } finally {
            if (deviceRefreshInFlight.current === refresh) deviceRefreshInFlight.current = null
        }
    }, [computers.backend, localComputer, state?.active])

    useEffect(() => {
        void refreshDevices()
    }, [refreshDevices])

    // While an invitation is on screen, the newly paired device should appear
    // on its own. Serialized reads every five seconds; stops with the panel.
    useEffect(() => {
        if (!pairing) return
        let cancelled = false
        let timeout: ReturnType<typeof globalThis.setTimeout> | undefined
        const poll = async () => {
            await refreshDevices()
            if (!cancelled) timeout = globalThis.setTimeout(() => void poll(), 5_000)
        }
        timeout = globalThis.setTimeout(() => void poll(), 5_000)
        return () => {
            cancelled = true
            if (timeout !== undefined) globalThis.clearTimeout(timeout)
        }
    }, [pairing, refreshDevices])

    const setEnabled = async (enabled: boolean) => {
        if (!api) return
        setSaving(true)
        setError(null)
        try {
            setState(await api.set(enabled))
            setPairing(null)
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setSaving(false)
        }
    }

    const restart = async () => {
        setRestarting(true)
        setError(null)
        try {
            await window.wildlarkAPI?.app.restart()
        } catch (cause) {
            setRestarting(false)
            setError(cause instanceof Error ? cause.message : String(cause))
        }
    }

    const toggleAddDevice = async () => {
        if (!localComputer) return
        if (pairing) {
            setPairing(null)
            return
        }
        setPairingBusy(true)
        setError(null)
        try {
            setPairing(await computers.startPairing(localComputer.nodeId))
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setPairingBusy(false)
        }
    }

    const revokeDevice = async (device: PairedDevice) => {
        if (!localComputer) return
        if (confirmTokenId !== device.tokenId) {
            setConfirmTokenId(device.tokenId)
            return
        }
        setRevokingTokenId(device.tokenId)
        setConfirmTokenId(null)
        setError(null)
        try {
            const report = await revokePairedDevice(computers.backend, localComputer.nodeId, device.tokenId)
            if (report.status === "done-with-gaps") {
                const detail = report.firstErrors.map((item) => `${item.system}: ${item.message}`).join("; ")
                throw new Error(`Device revocation is incomplete (${report.succeeded}/${report.requested} succeeded). Retry to finish: ${detail}`)
            }
            await refreshDevices()
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setRevokingTokenId(null)
        }
    }

    const reachability = reachabilityFor(api ? state : { enabled: false, active: false })
    const pendingRestart = state !== null && state.enabled !== state.active
    const guest = codeStore?.isGuest === true
    let order = 0

    return (
        <div className="flex flex-col gap-9">
            <header className="sharing-rise">
                <h2 className="text-2xl font-semibold tracking-tight text-base-content">Sharing</h2>
                <p className="mt-1 max-w-xl text-sm leading-6 text-muted">
                    Work with people you trust, on your own computers, without handing anyone the keys.
                </p>
            </header>

            {!guest && <IdentityHero />}

            <Section label="Computers" order={++order} description={localComputer ? undefined : "Computers this app can reach."}>
                <div className="grid gap-3 sm:grid-cols-2">
                    {localComputer && (
                        <LocalComputerCard
                            computer={localComputer}
                            reachability={reachability}
                            remoteSwitch={
                                api
                                    ? { checked: state?.enabled ?? false, disabled: saving || restarting, onChange: (enabled) => void setEnabled(enabled) }
                                    : null
                            }
                            pendingRestart={pendingRestart}
                            restarting={restarting}
                            onRestart={() => void restart()}
                            devices={devices}
                            devicesBusy={devicesBusy}
                            onRevokeDevice={(device) => void revokeDevice(device)}
                            revokingTokenId={revokingTokenId}
                            confirmTokenId={confirmTokenId}
                            canAddDevice={state?.active === true}
                            addDeviceBusy={pairingBusy}
                            addDeviceOpen={pairing !== null}
                            onAddDevice={() => void toggleAddDevice()}
                        />
                    )}
                    {ownRemoteComputers.map((computer) => (
                        <RemoteComputerCard key={computer.nodeId} computer={computer} onRemove={() => void computers.removeComputer(computer.nodeId)} />
                    ))}
                    <AddComputerTile />
                </div>
                {pairing && localComputer && (
                    <div className="mt-3">
                        <AddDevicePanel pairing={pairing} computerName={localComputer.name} onClose={() => setPairing(null)} />
                    </div>
                )}
            </Section>

            {ownedComputer && !guest && <PeopleList nodeId={ownedComputer.nodeId} nodeName={ownedComputer.name} order={++order} />}

            <SharedWithYou order={++order} />

            {error && (
                <div role="alert" className="border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                    {error}
                </div>
            )}

            <footer className="sharing-rise border-t border-border pt-5" style={{ animationDelay: `${(order + 1) * 70}ms` }}>
                <div className="flex items-start gap-3 text-xs leading-5 text-muted">
                    <LockKeyhole className="mt-0.5 h-3.5 w-3.5 flex-none text-success" />
                    <p className="max-w-xl">
                        <span className="font-medium text-base-content">Private relay.</span> Devices find each other through WildLark's relay, and every byte
                        between them is end-to-end encrypted. The relay never holds the keys to read your work.{" "}
                        <button
                            type="button"
                            className="btn text-base-content underline decoration-border underline-offset-2 hover:decoration-base-content"
                            onClick={() => setRelayDetails((value) => !value)}
                        >
                            {relayDetails ? "Less" : "How it works"}
                        </button>
                    </p>
                </div>
                {relayDetails && (
                    <div className="mt-3 max-w-xl space-y-2 pl-[26px] text-xs leading-5 text-muted">
                        <p>
                            This computer opens one outbound connection and registers an address derived from its P-256 public key. A client verifies that key,
                            then both sides derive fresh session keys with P-256 ECDH and HKDF-SHA-256. Every frame is protected with AES-256-GCM.
                        </p>
                        <p>
                            Pairing links, access tokens, requests, files, and responses stay inside that encrypted connection. The relay can see IP addresses,
                            timing, and traffic volume, but not the contents. Who may connect, and revoking them, is decided on this computer.
                        </p>
                    </div>
                )}
            </footer>
        </div>
    )
}
